import React, { useState, useEffect } from "react";
import { Box, Grid, TextField, Typography } from "@mui/material";

import Section from "../../../layout/Section";
import { CompanyWithShareholders } from "../../../../types";
import { colors } from "../../../../styles/colors";
import { borderRadius, formField } from "../../../../styles/theme";

interface CompanySectionProps {
  company: CompanyWithShareholders | null;
  canEdit: boolean;
  isSaving: boolean;
  onFieldChange: (field: string, value: string | null) => Promise<void>;
}

const CompanySection: React.FC<CompanySectionProps> = ({
  company,
  canEdit,
  onFieldChange,
}) => {
  const [localName, setLocalName] = useState("");
  const [localKomplementaer, setLocalKomplementaer] = useState("");

  useEffect(() => {
    setLocalName(company?.name ?? "");
  }, [company?.name]);
  useEffect(() => {
    setLocalKomplementaer(company?.komplementaer ?? "");
  }, [company?.komplementaer]);

  const labelWidth = 240;
  const inputMaxWidth = 280;

  // Höhe und Innenabstand kommen zentral aus `formField` (styles/theme.ts).
  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: `${borderRadius.field}px`,
      boxShadow: `0 2px 4px ${colors.shadow.light}`,
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: colors.border.focus,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: colors.border.focus,
        borderWidth: 2,
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderRadius: `${borderRadius.field}px`,
      },
    },
  };

  const readOnlyFieldSx = {
    "& .MuiOutlinedInput-root": {
      "& .MuiOutlinedInput-notchedOutline": {
        border: "none",
      },
    },
    // Ohne Umrandung auch ohne seitlichen Innenabstand, damit der Text bündig zum übrigen
    // Text der Seite steht – vertikal weiter der zentrale Wert aus `formField`.
    "& .MuiOutlinedInput-input": {
      padding: `${formField.paddingY} 0`,
    },
  };

  return (
    <Section title="Stammdaten">
      <Grid container spacing={3} sx={{ py: 1 }}>
        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography
              sx={{
                width: labelWidth,
                minWidth: labelWidth,
                whiteSpace: "nowrap",
              }}
            >
              Gesellschaftsname
            </Typography>
            <Box sx={{ flex: 1, maxWidth: inputMaxWidth }}>
              <TextField
                fullWidth
                variant="outlined"
                value={localName}
                onChange={(e) => setLocalName(e.target.value)}
                onBlur={() => onFieldChange("name", localName || null)}
                placeholder="z.B. Windpark Musterstadt"
                disabled={!canEdit}
                size="small"
                sx={canEdit ? textFieldSx : readOnlyFieldSx}
              />
            </Box>
          </Box>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography
              sx={{
                width: labelWidth,
                minWidth: labelWidth,
                whiteSpace: "nowrap",
              }}
            >
              Rechtsform
            </Typography>
            <Box sx={{ flex: 1, maxWidth: inputMaxWidth }}>
              <TextField
                fullWidth
                variant="outlined"
                value={company?.rechtsform ?? "KG"}
                slotProps={{ input: { readOnly: true } }}
                size="small"
                sx={readOnlyFieldSx}
              />
            </Box>
          </Box>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography
              sx={{
                width: labelWidth,
                minWidth: labelWidth,
                whiteSpace: "nowrap",
              }}
            >
              Komplementär
            </Typography>
            <Box sx={{ flex: 1, maxWidth: inputMaxWidth }}>
              <TextField
                fullWidth
                variant="outlined"
                value={localKomplementaer}
                onChange={(e) => setLocalKomplementaer(e.target.value)}
                onBlur={() =>
                  onFieldChange("komplementaer", localKomplementaer || null)
                }
                placeholder="z.B. Windpark Musterstadt Verwaltungs-GmbH"
                disabled={!canEdit}
                size="small"
                sx={canEdit ? textFieldSx : readOnlyFieldSx}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Section>
  );
};

export default React.memo(CompanySection);
